import Notification from '@rio-cloud/rio-uikit/Notification';
import { ChangeEvent, KeyboardEventHandler, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import CharacterCounter from '@/components/atom/CharacterCounter/CharacterCounter';
import { ChatComposerFocusContext } from '@/components/molecule/ChatInput/ChatComposerFocusContext';
import {
  ControlsColumn,
  ErrorMessage,
  ImgSrc,
  InputContainer,
  SendButton,
  SlotFadeWrapper,
  StyledInput,
  StyledInputWrapper,
  SuggestionOverlay,
  WarningMessage,
} from '@/components/molecule/ChatInput/ChatInput.styles';
import { useComposerAutoFocus } from '@/components/molecule/ChatInput/useComposerAutoFocus';
import { getLocale } from '@/configuration/lang/langSlice';
import { AUTOCOMPLETE_ACTIONS, AUTOCOMPLETE_CATEGORY, gaPush } from '@/configuration/setup/googleAnalytics';
import { useAppSelector } from '@/configuration/setup/hooks';
import { useIsDemoThread } from '@/hooks/useIsDemoThread';
import { isCurrentRoute } from '@/routes/routeHelper';
import { selectActiveThreadId } from '@/store/thread/threadSlice';
import { fetchSuggestions } from '@/utils/suggestions/fetchSuggestions';

type ChatInputProps = {
  isLoading: boolean;
  onSendMessage: (message: string) => void;
  onSendFile?: (file: File) => void;
  //TEMP:  Slot for Example Questions link until FIBOT-1738 introduces proper example questions controllers
  bottomLeftSlot?: ReactNode;
};

const validationRules = {
  maxMessageLength: 500,
  allowedFileType: 'application/pdf',
  maxFileSizeMB: 5,
};

const errorMessages = {
  messageTooLong: 'chatPage.chatInput.validation.tooLong.error',
  invalidFileType: 'chatPage.chatInput.validation.invalidType.error',
  fileTooLarge: 'chatPage.chatInput.validation.tooLarge.error',
};

const warningMessages = {
  format: 'chatPage.chatInput.warning.format',
  multipleTimePeriods: 'chatPage.chatInput.warning.multipleTimePeriods',
  longTimePeriods: 'chatPage.chatInput.warning.longTimePeriods',
  buzzwords: 'chatPage.chatInput.warning.buzzwords',
};

const checkKeyWords = {
  format: 'chatPage.chatInput.keyWords.format',
  lastWeeks: 'chatPage.chatInput.keyWords.lastWeeks',
  lastMonths: 'chatPage.chatInput.keyWords.lastMonths',
  lastYears: 'chatPage.chatInput.keyWords.lastYears',
  everyPeriod: 'chatPage.chatInput.keyWords.everyPeriod',
  monthsNames: 'chatPage.chatInput.keyWords.months',
  buzzwords: 'chatPage.chatInput.keyWords.buzzwords',
};

const ChatInput = ({ isLoading, onSendMessage, onSendFile, bottomLeftSlot }: ChatInputProps) => {
  const intl = useIntl();
  const activeThreadId = useAppSelector(selectActiveThreadId);
  const isOnBoardingLocation = isCurrentRoute('/onboarding');
  const isDemoThread = useIsDemoThread(activeThreadId) || isOnBoardingLocation;

  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [caretTop, setCaretTop] = useState(0);
  const [caretLeft, setCaretLeft] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeout = useRef<number | undefined>(undefined);
  const lastSuggestionsRef = useRef<string[]>([]);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const sendButtonRef = useRef<HTMLButtonElement>(null);

  const [hasAcceptedSuggestion, setHasAcceptedSuggestion] = useState(false);

  const currentLanguage = useSelector(getLocale);

  const placeholderText = intl.formatMessage({ id: 'chatPage.chatInput.placeholder' });

  const showErrorMessage = (errorMessage: string) => {
    setError(errorMessage);
    Notification.error(<span>{intl.formatMessage({ id: errorMessage })}</span>);
  };

  const validateFile = (file: File): boolean => {
    if (file.type !== validationRules.allowedFileType) {
      showErrorMessage(errorMessages.invalidFileType);
      return false;
    }
    if (file.size > validationRules.maxFileSizeMB * 1024 * 1024) {
      showErrorMessage(errorMessages.fileTooLarge);
      return false;
    }
    return true;
  };

  const sanitizeInput = (value: string) => value.replace(/[<>{}]/g, '');

  const validateInput = (value: string) => {
    if (value.length > validationRules.maxMessageLength) {
      showErrorMessage(errorMessages.messageTooLong);
      return false;
    }
    return true;
  };

  const checkInput = (value: string) => {
    const formatKeyWords = intl
      .formatMessage({ id: checkKeyWords.format })
      .split(',')
      .map((item) => item.trim().toLowerCase());
    const buzzwords = intl
      .formatMessage({ id: checkKeyWords.buzzwords })
      .split(',')
      .map((item) => item.trim().toLowerCase());

    const lastWeeksKeyWordsPattern = `\\b${intl.formatMessage({ id: checkKeyWords.lastWeeks }, { weeksNumber: '((?:[2-9]|d{2,})|[a-z]+)' }).toLocaleLowerCase()}\\b`;
    const lastWeeksKeyWordsRegex = new RegExp(lastWeeksKeyWordsPattern, 'gi');

    const lastMonthsKeyWordsPattern = `\\b${intl.formatMessage({ id: checkKeyWords.lastMonths }, { monthsNumber: '((?:[2-9]|\\\\d{2,})|[a-z]+)' }).toLocaleLowerCase()}\\b`;
    const lastMonthsKeyWordsRegex = new RegExp(lastMonthsKeyWordsPattern, 'gi');

    const lastYearsKeyWordsPattern = `\\b${intl.formatMessage({ id: checkKeyWords.lastYears }, { yearsNumber: '((?:[2-9]|d{2,})|[a-z]+)' }).toLocaleLowerCase()}\\b`;
    const lastYearsKeyWordsRegex = new RegExp(lastYearsKeyWordsPattern, 'gi');

    const everyKeyWords = intl
      .formatMessage({ id: checkKeyWords.everyPeriod })
      .split(',')
      .map((item) => item.trim().toLowerCase());

    const monthsPattern = intl
      .formatMessage({ id: checkKeyWords.monthsNames })
      .split(',')
      .map((item) => item.trim().toLowerCase())
      .join('|');

    const yearPattern = new RegExp(
      `\\b\\d{4}\\b(?![-\\/\\.\\s]\\d{2}|\\s(?:${monthsPattern})|[-\\/\\.\\s]\\d{1,2}[-\\/\\.\\s]\\d{1,2}|\\s\\d{1,2}\\s(?:${monthsPattern}))`,
      'i',
    );

    const dateRegex1 = /\b\d{1,2}[-\/\.\s]\d{1,2}[-\/\.\s]\d{4}\b/;
    const dateRegex2 = new RegExp(`\\b\\d{1,2}\\s(?:${monthsPattern})\\s\\d{4}\\b`, 'i');
    const dateRegex3 = new RegExp(`\\b(?:${monthsPattern})\\s\\d{1,2},\\s\\d{4}\\b`, 'i');
    const dateRegex4 = new RegExp(`\\b(?:${monthsPattern})\\s\\d{4}\\b`, 'i');

    const lowerValue = value.toLocaleLowerCase();

    const containsMultipleTimePeriods = () =>
      lastWeeksKeyWordsRegex.test(lowerValue) ||
      lastMonthsKeyWordsRegex.test(lowerValue) ||
      lastYearsKeyWordsRegex.test(lowerValue) ||
      everyKeyWords.some((word) => lowerValue.includes(word)) ||
      new RegExp(monthsPattern, 'i').test(lowerValue);

    const isLongTimePeriod = () =>
      yearPattern.test(lowerValue) &&
      ![dateRegex1, dateRegex2, dateRegex3, dateRegex4].some((regex) => regex.test(lowerValue));

    if (formatKeyWords.some((word) => lowerValue.includes(word))) {
      setWarning(warningMessages.format);
    } else if (buzzwords.some((word) => lowerValue.includes(word))) {
      setWarning(warningMessages.buzzwords);
    } else if (containsMultipleTimePeriods()) {
      setWarning(warningMessages.multipleTimePeriods);
    } else if (isLongTimePeriod()) {
      setWarning(warningMessages.longTimePeriods);
    } else {
      setWarning('');
    }
  };

  const handleSend = () => {
    const isSendable = inputValue.trim().length > 0 && !isLoading && !isDemoThread;
    if (!isSendable) return;

    if (validateInput(inputValue)) {
      onSendMessage(inputValue);

      if (!hasAcceptedSuggestion && lastSuggestionsRef.current.length > 0) {
        const typedSuggestionManually = lastSuggestionsRef.current.some(
          (suggestion) => suggestion.trim().toLowerCase() === inputValue.trim().toLowerCase(),
        );

        if (typedSuggestionManually) {
          gaPush({
            category: AUTOCOMPLETE_CATEGORY,
            action: AUTOCOMPLETE_ACTIONS.TYPED_MANUALLY,
            label: inputValue,
          });
        }
      }

      setWarning('');
      setInputValue('');
      setSuggestions([]);
      setActiveSuggestionIndex(-1);
      setHasAcceptedSuggestion(false);
    }
  };

  const applySuggestion = (suggestionText: string) => {
    const lines = inputValue.split('\n');
    const lastLine = lines[lines.length - 1];
    const lastWord = lastLine.trim().split(/\s+/).pop()?.toLowerCase() ?? '';

    let completion = suggestionText.trim();
    if (completion.toLowerCase().startsWith(lastWord) && lastWord.length > 0) {
      completion = completion.substring(lastWord.length);
    }

    lines[lines.length - 1] = lastLine + completion;

    const newInputValue = lines.join('\n');

    setInputValue(newInputValue);
    setSuggestions([]);
    setActiveSuggestionIndex(-1);
    setHasAcceptedSuggestion(true);

    gaPush({
      category: AUTOCOMPLETE_CATEGORY,
      action: AUTOCOMPLETE_ACTIONS.ACCEPT_SUGGESTION,
      label: suggestionText,
    });
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    if (e.shiftKey) {
      return;
    }

    if (e.key === 'Backspace') {
      return;
    }

    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSuggestionIndex((prevIndex) => {
          const newIndex = prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0;

          gaPush({
            category: AUTOCOMPLETE_CATEGORY,
            action: AUTOCOMPLETE_ACTIONS.ARROW_NAVIGATION,
            label: `Index: ${newIndex}`,
          });

          return newIndex;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSuggestionIndex((prevIndex) => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1;

          gaPush({
            category: AUTOCOMPLETE_CATEGORY,
            action: AUTOCOMPLETE_ACTIONS.ARROW_NAVIGATION,
            label: `Index: ${newIndex}`,
          });

          return newIndex;
        });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (!isLoading) {
          handleSend();
        }
      } else if (e.key === 'Tab') {
        if (activeSuggestionIndex >= 0) {
          e.preventDefault();
          applySuggestion(suggestions[activeSuggestionIndex]);
        }
      }
    } else if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    const value = target.value;
    checkInput(value);

    if (target.files && target.files[0]) {
      const file = target.files[0];
      if (validateFile(file)) {
        if (onSendFile) onSendFile(file);
      }
      target.value = '';
    } else {
      const sanitizedValue = sanitizeInput(value.slice(0, validationRules.maxMessageLength));
      setInputValue(sanitizedValue);
      if (error) {
        setError('');
      }

      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      debounceTimeout.current = window.setTimeout(() => {
        const words = sanitizedValue.split(/\s+/);
        const lastWord = words[words.length - 1];

        const newSuggestions = fetchSuggestions(lastWord, currentLanguage || 'en-GB');

        if (newSuggestions.length > 0) {
          gaPush({
            category: AUTOCOMPLETE_CATEGORY,
            action: AUTOCOMPLETE_ACTIONS.SUGGESTIONS_SHOWN,
            label: newSuggestions.join(', '),
          });

          lastSuggestionsRef.current = newSuggestions;
        }

        setSuggestions(newSuggestions);
        setActiveSuggestionIndex(newSuggestions.length > 0 ? 0 : -1);
      }, 300);
    }
  };

  //INFO: Calculate caret position for the suggestion overlay alignment
  const updateCaretPosition = () => {
    const ta = textAreaRef.current;
    if (!ta) return;

    const style = getComputedStyle(ta);
    const lineHeight = parseInt(style.lineHeight || '15', 10);
    const paddingTop = parseInt(style.paddingTop || '0', 10);
    const paddingLeft = parseInt(style.paddingLeft || '0', 10);

    const textBeforeCaret = ta.value.substring(0, ta.selectionStart);
    const lines = textBeforeCaret.split('\n');

    const linesBeforeCaret = lines.length - 1;
    const currentLine = lines[lines.length - 1];

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.font = `${style.fontSize} ${style.fontFamily}`;
    const textWidth = ctx.measureText(currentLine).width;

    const caretTop = linesBeforeCaret * lineHeight + paddingTop - ta.scrollTop - 2;
    const caretLeft = textWidth + paddingLeft - ta.scrollLeft;

    setCaretTop(caretTop);
    setCaretLeft(caretLeft);
  };

  useEffect(() => {
    //INFO: Close suggestions on outside click
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        if (suggestions.length > 0) {
          setSuggestions([]);
          setActiveSuggestionIndex(-1);

          if (!hasAcceptedSuggestion) {
            gaPush({
              category: AUTOCOMPLETE_CATEGORY,
              action: AUTOCOMPLETE_ACTIONS.CLOSE_WITHOUT_USE,
              label: 'Click outside',
            });
          }
        }
      }
    };

    // INFO: Dismiss suggestions on Escape only when the Message Input is focused
    const handleRawEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && suggestions.length > 0 && e.target === textAreaRef.current) {
        setSuggestions([]);
        setActiveSuggestionIndex(-1);

        if (!hasAcceptedSuggestion) {
          gaPush({
            category: AUTOCOMPLETE_CATEGORY,
            action: AUTOCOMPLETE_ACTIONS.CLOSE_WITHOUT_USE,
            label: 'Escape key',
          });
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('keydown', handleRawEscape);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleRawEscape);
    };
  }, [suggestions, hasAcceptedSuggestion]);

  useEffect(() => {
    updateCaretPosition();
  }, [inputValue, activeSuggestionIndex]);

  const currentSuggestion =
    suggestions.length > 0 && activeSuggestionIndex >= 0 ? suggestions[activeSuggestionIndex] : '';

  const words = inputValue.split(/\s+/);
  const lastWord = words[words.length - 1].toLowerCase();

  let remainder = currentSuggestion.trim();
  if (currentSuggestion.toLowerCase().startsWith(lastWord) && lastWord.length > 0) {
    remainder = currentSuggestion.substring(lastWord.length);
  }

  const isDisabled = isDemoThread || isLoading;
  const canSend = inputValue.trim().length > 0 && !isLoading && !isDemoThread;
  const isAtLimit = inputValue.length >= validationRules.maxMessageLength;

  useComposerAutoFocus({ textAreaRef, sendButtonRef, isDisabled });

  //INFO: Focuses the Message Input textarea. Provided via context to child components.
  const focusMessageInput = useCallback(() => {
    textAreaRef.current?.focus();
  }, []);

  const hasDraft = inputValue.length > 0;

  const composerFocusValue = useMemo(() => ({ focusMessageInput, hasDraft }), [focusMessageInput, hasDraft]);

  return (
    <ChatComposerFocusContext.Provider value={composerFocusValue}>
      <div>
        {warning && (
          <WarningMessage>
            <ImgSrc className="rioglyph rioglyph-info-sign padding-right-3" />
            {intl.formatMessage({ id: warning })}
          </WarningMessage>
        )}
        <InputContainer
          $disabled={isDisabled}
          $warning={isAtLimit}
          aria-busy={isDisabled}
          role="form"
          aria-label="Chat message form"
        >
          <StyledInputWrapper ref={inputRef}>
            <StyledInput
              ref={textAreaRef}
              id="chat-input"
              rows={1}
              readOnly={isDisabled}
              aria-disabled={isDisabled}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              maxLength={validationRules.maxMessageLength}
              autoComplete="off"
              aria-autocomplete="list"
              aria-controls="suggestions-list"
              aria-expanded={suggestions.length > 0}
              aria-haspopup="listbox"
              aria-label={placeholderText}
              data-testid="input-send-message"
              onClick={updateCaretPosition}
              onKeyUp={updateCaretPosition}
              placeholder={placeholderText}
            />
            {currentSuggestion && remainder && (
              <SuggestionOverlay caretLeft={caretLeft} caretTop={caretTop}>
                {remainder}
              </SuggestionOverlay>
            )}
          </StyledInputWrapper>
          <ControlsColumn>
            {/* INFO: Margin keeps the counter visually aligned with the button, which has a border-radius */}
            <div className="margin-right-5">
              <CharacterCounter
                current={inputValue.length}
                max={validationRules.maxMessageLength}
                isAtLimit={isAtLimit}
              />
            </div>
            <SendButton
              ref={sendButtonRef}
              type="button"
              $active={canSend}
              onClick={() => handleSend()}
              aria-disabled={!canSend}
              aria-label="Send message"
              data-testid="send-button"
            >
              <FormattedMessage id="chatPage.chatInput.button.send" />
              <span className="rioglyph rioglyph-send" />
            </SendButton>
          </ControlsColumn>
          <SlotFadeWrapper $hidden={hasDraft}>{bottomLeftSlot}</SlotFadeWrapper>
        </InputContainer>
        {error && <ErrorMessage>{intl.formatMessage({ id: error })}</ErrorMessage>}
      </div>
    </ChatComposerFocusContext.Provider>
  );
};

export default ChatInput;
